'use client';

import { useState, useCallback, useEffect } from 'react';
import { useAuthBoot } from '../context/AuthBootContext';
import { CONSENT_STORAGE_KEY, SHAKE_ANIMATION_DURATION_MS } from '../constants';
import { FeatureFlags } from '@/lib/consts/featureFlags';

interface IUseConsentReturn {
    hasConsent: boolean;
    shouldShowAlert: boolean;
    isAlertShaking: boolean;
    isConsentRequired: boolean;
    isConsentV2: boolean;
    handleConsentChange: (isConsented: boolean) => void;
    handleOverlayClick: () => void;
    shakeConsent: () => void;
}

export function useConsent(): IUseConsentReturn {
    const [hasConsent, setHasConsentState] = useState(false);
    const [shouldShowAlert, setShouldShowAlert] = useState(false);
    const [isAlertShaking, setIsAlertShaking] = useState(false);

    const { authBoot } = useAuthBoot();
    const isAdvancedTextFeatureEnabled = authBoot.featureFlags?.[FeatureFlags.DASHBOARD_NEW_PRIVACY_RICH_TEXT] ?? false;
    const isConsentRequired = isAdvancedTextFeatureEnabled && (authBoot.storeTheme.login?.consentSection?.consentRequired || false);
    const isConsentV2 = authBoot.featureFlags?.[FeatureFlags.STORE_LOGIN_CONSENT_V2] ?? false;

    const handleConsentChange = useCallback((isConsented: boolean) => {
        setHasConsentState(isConsented);
        if (isConsented) {
            localStorage.setItem(CONSENT_STORAGE_KEY, 'true');
            setShouldShowAlert(false);
        } else {
            localStorage.removeItem(CONSENT_STORAGE_KEY);
        }
    }, []);

    const shakeConsent = useCallback(() => {
        setIsAlertShaking(true);
        setTimeout(() => setIsAlertShaking(false), SHAKE_ANIMATION_DURATION_MS);
    }, []);

    const handleOverlayClick = useCallback(() => {
        if (isConsentV2) return;
        if (shouldShowAlert) {
            setIsAlertShaking(true);
            setTimeout(() => setIsAlertShaking(false), SHAKE_ANIMATION_DURATION_MS);
        } else {
            setShouldShowAlert(true);
        }
    }, [shouldShowAlert, isConsentV2]);

    useEffect(() => {
        const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
        if (stored === 'true') {
            setHasConsentState(true);
        }
    }, []);

    return {
        hasConsent,
        shouldShowAlert,
        isAlertShaking,
        isConsentRequired,
        isConsentV2,
        handleConsentChange,
        handleOverlayClick,
        shakeConsent,
    };
}
